<script setup>
    import { defineExpose, defineEmits, ref, inject, computed, getCurrentInstance } from 'vue';
    import { JobcategoryInfoRequest } from '@/models/management/managementModel';
    import { useManagementStore } from '@/stores/management/managementStore';
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 利用者の管理ストア
    const managementStore = useManagementStore();
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // 詳細リクエスト項目
    const jobCategoryInfoRequest = ref({});

    // 入力欄活性コントロール
    // 基本情報
    const disabled = computed(() => {
        return jobCategoryInfoRequest.value.TouDataFlag;
    });

    // ID入力欄の活性/非活性の状態を制御する。初期値は活性で入力可能。
    const idInputDisabled = ref(false);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 初期処理 親画面から利用可能関数
     */
    async function setInfo(JobCategoryPk) {
        // 初期化する。
        idInputDisabled.value = false;

        // 詳細リクエスト項目の初期化
        jobCategoryInfoRequest.value = new JobcategoryInfoRequest();
        if (JobCategoryPk) {
            jobCategoryInfoRequest.value.JobCategoryPk = JobCategoryPk;
            // 詳細取得
            await getDetail(jobCategoryInfoRequest.value.JobCategoryPk);

            // APIから取得した契約割り当てが未設定の場合は、nullもしくは空配列が返却されるので、モデルクラス上は空配列で設定する。
            if (Utils.isNullEmptyArray(jobCategoryInfoRequest.value.CmpContPks)) {
                jobCategoryInfoRequest.value.CmpContPks = [];
            }

            // 編集モードの場合は、IDの入力欄は非活性にする。
            idInputDisabled.value = true;
        }
        // 企業契約情報取得
        await getCintractList();

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalMasterDetail .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }
    defineExpose({
        setInfo,
    });

    /**
     * 詳細情報処理
     */
    async function getDetail(JobCategoryPk) {
        const queryParam = {
            JobCategoryPk: JobCategoryPk,
        };
        const response = await managementStore.getJobDtlAction(API_URL, queryParam);
        if (response.success) {
            jobCategoryInfoRequest.value = Object.assign(jobCategoryInfoRequest.value, managementStore.jobCategorynInfo);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 登録or更新処理
     */
    async function createDetail() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        //const requestBody = jobCategoryInfoRequest.value;

        let requestBody = jobCategoryInfoRequest.value;

        // 表示順が設定されていない場合はnullを設定する。
        if (Utils.isNullEmpty(requestBody.DisplayOrder)) {
            requestBody.DisplayOrder = null;
        }

        // 契約割り当てが選択されていない場合はnullを設定する。
        if (Utils.isNullEmptyArray(requestBody.CmpContPks)) {
            requestBody.CmpContPks = null;
        }

        if (jobCategoryInfoRequest.value.JobCategoryPk === '') {
            const response = await managementStore.postJobDtlAction(API_URL, requestBody);
            if (response.success) {
                closeAndUpdate();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            const response = await managementStore.putJobDtlAction(API_URL, requestBody);
            if (response.success) {
                closeAndUpdate();
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';
        // ID
        if (Utils.isNullEmpty(jobCategoryInfoRequest.value.JobCategoryId)) {
            message += i18n.global.t('ManagementJobCategoryDetail.message.message1') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfUserId(jobCategoryInfoRequest.value.JobCategoryId)) {
                message += i18n.global.t('ManagementJobCategoryDetail.message.message2') + '\n';
            }
            // 入力可能文字のチェック
            if (!Utils.isMatchPatternAlphabetNumberSymbolForManagementId(jobCategoryInfoRequest.value.JobCategoryId)) {
                message += i18n.global.t('ManagementJobCategoryDetail.message.message7') + '\n';
            }
        }
        // 日本語名
        if (Utils.isNullEmpty(jobCategoryInfoRequest.value.JobCategoryNmJ)) {
            message += i18n.global.t('ManagementJobCategoryDetail.message.message3') + '\n';
        } else {
            // 文字数
            if (Utils.checkLengthOfManagementShortNameString(jobCategoryInfoRequest.value.JobCategoryNmJ)) {
                message += i18n.global.t('ManagementJobCategoryDetail.message.message4') + '\n';
            }
        }
        // 英語名
        if (!Utils.isNullEmpty(jobCategoryInfoRequest.value.JobCategoryNmE)) {
            // 文字数
            if (Utils.checkLengthOfManagementShortNameString(jobCategoryInfoRequest.value.JobCategoryNmE)) {
                message += i18n.global.t('ManagementJobCategoryDetail.message.message5') + '\n';
            }
            // 入力可能文字のチェック
            if (!Utils.isMatchPatternAlphabetNumberSymbolForManagementNameEn(jobCategoryInfoRequest.value.JobCategoryNmE)) {
                message += i18n.global.t('ManagementJobCategoryDetail.message.message8') + '\n';
            }
        }
        // 表示順
        if (!Utils.isNullEmpty(jobCategoryInfoRequest.value.DisplayOrder)) {
            // 文字数
            if (Utils.checkLengthOfDisplayOrderNumber(jobCategoryInfoRequest.value.DisplayOrder)) {
                message += i18n.global.t('ManagementJobCategoryDetail.message.message6') + '\n';
            }
        }
        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 企業契約情報取得処理
     */
    async function getCintractList() {
        const queryParam = {};
        const response = await managementStore.getCmpConAction(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 親画面で実装必須関数
     */
    const emit = defineEmits(['detailModal:cancel', 'detailModal:closeAndUpdate']);
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('detailModal:cancel');
    }
    function closeAndUpdate() {
        // 親画面の、モーダルを閉じて、一覧画面の再描画を行う処理を呼び出す。
        emit('detailModal:closeAndUpdate');
    }
</script>

<template>
    <article id="modalMasterDetail" class="modal" style="display: block" :style="{ display: 'block' }">
        <h3 class="textCenter">{{ $t('ManagementJobCategoryDetail.parts.pageTitle1') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <section class="form dtWidth170">
                <h4>{{ $t('ManagementJobCategoryDetail.parts.pageTitle2') }}</h4>
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementJobCategoryDetail.parts.dl2') }} </label>
                            </li>
                            <li class="must">
                                <span>{{ $t('ManagementJobCategoryDetail.parts.dl1') }} </span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" v-model="jobCategoryInfoRequest.JobCategoryId" :disabled="idInputDisabled" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementJobCategoryDetail.parts.dl3') }} </label>
                            </li>
                            <li class="must">
                                <span>{{ $t('ManagementJobCategoryDetail.parts.dl1') }} </span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" v-model="jobCategoryInfoRequest.JobCategoryNmJ" :disabled="disabled" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementJobCategoryDetail.parts.dl4') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="text" v-model="jobCategoryInfoRequest.JobCategoryNmE" :disabled="disabled" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementJobCategoryDetail.parts.dl5') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <input type="number" v-model="jobCategoryInfoRequest.DisplayOrder" :disabled="disabled" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <h4 style="margin-top: 40px">{{ $t('ManagementJobCategoryDetail.parts.pageTitle3') }}</h4>
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('ManagementJobCategoryDetail.parts.dl6') }} </label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <section class="innerTable" style="height: 329px">
                            <table class="table">
                                <tbody>
                                    <tr v-for="(data, index) in managementStore.contractList" :key="index">
                                        <td style="width: 20px">
                                            <input type="checkbox" :id="'data5-' + (index + 1)" class="checkbox" v-model="jobCategoryInfoRequest.CmpContPks" :value="data.CmpContPk" :disabled="data.TouDataFlag" />
                                            <label class="checkbox" :for="'data5-' + (index + 1)" />
                                        </td>
                                        <td style="width: auto">
                                            <span>{{ index + 1 }}. {{ data.CmpContNm }} ({{ data.CmpContPeriodS }} - {{ data.CmpContPeriodE }})</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </section>
                    </dd>
                </dl>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal closeModal" @click="cancel()">{{ $t('ManagementJobCategoryDetail.parts.button1') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary closeModal" @click="createDetail()" :disabled="disabledStore.isDisabled">{{ $t('ManagementJobCategoryDetail.parts.button2') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>

<style></style>
